import moment from 'moment'
import clsx from 'clsx'
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import config from '@/config'
import documentExportConfig from '@/modules/document-export/config'
import request from '@/modules/base/util/request'
import AddIcon from '@assets/images/icons/add.svg'
import { ButtonIconSquare } from '@/modules/base/components/buttons/index'
import { DateInput, SelectInput } from '@/modules/base/components/inputs/index'
import { fetchDocumentExports } from '@/redux/ducks/documentExport/list'
import { SubTitle, Title } from '@/modules/base/components/texts'
import newExportIcon from '@assets/images/icons/exportNew.svg'

import { toast } from '@/modules/base/components/toast/toastConfig'
import getPeriodOptions from './helpers/getPeriodOptions'
import { FinanceFormKeys } from '@/modules/document-export/constants'
import { TAG_CATEGORY } from '@/modules/base/components/inputs/tag-select-input/tag-select-input'
import { TagService } from '@/modules/tags/tagService'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import { SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import { CLIENT_INVITATION_STATUS } from '@/modules/client/_helpers/getStatusLabel'
import { useAccountSettings } from '@/modules/settings/components/tax-settings/services/account-setting-provider'
import { TAG_TYPES } from '@/modules/file/constants'

const { dateFormat: { api: apiDateFormat } } = config
const defaultStartDate = moment().startOf('year').format(apiDateFormat)
const defaultEndDate = moment().endOf('year').format(apiDateFormat)

const useStyles = makeStyles((theme) => ({
  selectInput: {
    width: 250,
  },
  startDateInput: {
    width: '50%',
  },
  endDateInput: {
    width: '50%',
  },
  action: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingTop: 30,
  },
  inputRowEnd: {
    justifyContent: 'flex-end',
  },
  inputFields: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 20,
  },
  subTitle: {
    lineHeight: '1.86',
    color: theme.black,
  },
}))

const DocumentExportForm = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { refresh } = useAccountSettings()
  const hasDocumentsRights = useCheckModuleAccess(SUBSCRIPTION_MODULES.DOCUMENTS)
  const hasFinanceFormExportDocumentsRights = useCheckModuleAccess(SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_DOCUMENTS)
  const { taxAccountant } = useSelector(state => state.documentExport.list)

  const { subscriptions } = useSelector(state => state.subscription.details)
  const isTaxAccountant = Boolean(subscriptions.find(sub => sub.isTaxAccountant))

  const { readOnly, client } = props

  const isOfflineClient = client?.inviteeStatus === CLIENT_INVITATION_STATUS.OFFLINE

  const [kindOptions, setKindOptions] = useState([])
  const [kindState, setKindState] = useState({
    ...taxAccountant,
    kind: {},
    kindPeriod: '',
    kindStartDate: defaultStartDate,
    kindEndDate: defaultEndDate,
  })

  useEffect(() => {
    if (((client && client.isAllowed) || !client) && !isOfflineClient) {
      refresh()
    }
  }, [client])

  useEffect(() => {
    setKindState({ ...kindState, ...taxAccountant })
  }, [taxAccountant])

  useEffect(() => {
    TagService.fetchTags(TAG_CATEGORY.DOCUMENTS).then(({ body: { data: options } }) => {
      setKindOptions(options.filter(option => option?.isSystem))
      setKindState({ ...kindState, kind: options.find((value) => { return value.tagType === TAG_TYPES.NOT_ASSIGNED }) })
    })
  }, [])

  const handleKindPeriodChange = (option) => {
    const { value, startDate, endDate } = option
    setKindState({ ...kindState, kindPeriod: value, kindStartDate: startDate, kindEndDate: endDate })
  }

  const handleKindStartDateChange = (value) => {
    const { kindEndDate } = kindState

    const newState = {
      kindStartDate: value,
    }

    if (moment(value).isAfter(kindEndDate)) {
      newState.kindEndDate = value
    }

    setKindState({ ...kindState, ...newState })
  }

  const handleKindEndDateChange = (value) => {
    const { kindStartDate } = kindState

    const newState = {
      kindEndDate: value,
    }

    if (moment(kindStartDate).isAfter(value)) {
      newState.kindStartDate = value
    }

    setKindState({ ...kindState, ...newState })
  }

  const handleKindChange = (value) => {
    setKindState({ ...kindState, kind: value })
  }

  const handleKindCreateClick = () => {
    const { kind, kindPeriod, kindStartDate, kindEndDate } = kindState
    const headers = {}
    if (client?.id) headers['collaboration-id'] = client.id

    request(documentExportConfig.accountantExportUrl, {
      auth: true,
      method: 'POST',
      data: {
        startDate: moment(kindStartDate).format(apiDateFormat),
        endDate: moment(kindEndDate).format(apiDateFormat),
        exportPeriod: kindPeriod,
        exportType: 'DOCUMENTS',
        filter: [kind.tagType === TAG_TYPES.CUSTOM ? kind.label : kind.tagType],
      },
      headers,
    })
      .then(() => {
        toast.showToast({ message: lang.documentExport.createSuccess })
        dispatch(fetchDocumentExports(client?.id))

        if (kindPeriod === 'custom') {
          setKindState({ kind: {}, kindPeriod: undefined, kindStartDate: defaultStartDate, kindEndDate: defaultEndDate })
        }
        navigate('/document-export#exportList', { replace: true })
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.documentExport.createError }))
  }

  const { kind, kindPeriod, kindStartDate, kindEndDate } = kindState

  const submitKindButtonDisabled = !kindPeriod || (kindPeriod === 'custom' && (!kindStartDate || !kindEndDate))
  const periodOptions = getPeriodOptions()

  const customKindDateInputs = kindPeriod === 'custom'
    ? (
      <div className={classes.inputFields}>
        <DateInput
          name='kindStartDate'
          label={lang.generics.startDate}
          value={kindStartDate}
          readOnly={readOnly}
          customClass={classes.startDateInput}
          onChange={handleKindStartDateChange}
        />
        <DateInput
          name='kindEndDate'
          label={lang.generics.endDate}
          value={kindEndDate}
          readOnly={readOnly}
          customClass={classes.endDateInput}
          onChange={handleKindEndDateChange}
        />
      </div>
    )
    : null

  const content = (
    !isTaxAccountant
      ? (<>
        <Title icon={newExportIcon}>{lang.documentExport.forms.export.title}</Title>
        <SubTitle className={classes.subTitle}>{lang.documentExport.forms.export.subTitle}</SubTitle>
        {hasFinanceFormExportDocumentsRights && hasDocumentsRights && <>
          <div className={classes.inputRow}>
            <div className={classes.inputFields}>
              <SelectInput
                name='kind'
                notAsync
                allowCreate={false}
                label={lang.documentExport.forms.export.documentTypes}
                value={kind}
                readOnly={readOnly}
                options={kindOptions}
                containerClass={classes.selectInput}
                customSelectOptions={{
                  isClearable: false,
                  valueKey: 'id',
                }}
                handleChange={handleKindChange}
              />
              <SelectInput
                name='kindPeriod'
                notAsync
                allowCreate={false}
                label={lang.documentExport.forms.export.periodLabel}
                value={kindPeriod}
                readOnly={readOnly}
                options={periodOptions}
                containerClass={classes.selectInput}
                customSelectOptions={{
                  placeholder: lang.documentExport.forms.export.periodPlaceholder,
                }}
                handleChange={handleKindPeriodChange}
              />
              {customKindDateInputs}
            </div>
            {!customKindDateInputs && <div className={classes.action}>
              <ButtonIconSquare
                disabled={submitKindButtonDisabled || readOnly}
                icon={AddIcon}
                label={lang.generics.make}
                onClick={handleKindCreateClick}
              />
            </div>}
          </div>
          {customKindDateInputs && <div className={clsx(classes.inputRow, classes.inputRowEnd)}>
            <div className={classes.action}>
              <ButtonIconSquare
                disabled={submitKindButtonDisabled || readOnly}
                icon={AddIcon}
                label={lang.generics.make}
                onClick={handleKindCreateClick}
              />
            </div>
          </div>}
        </>}
      </>)
      : (<>
        <Title icon={newExportIcon}>{lang.documentExport.forms.export.title}</Title>
        <SubTitle className={classes.subTitle}>{lang.documentExport.forms.export.subTitle}</SubTitle>
        <div className={classes.inputRow}>
          <div className={classes.inputFields}>
            <SelectInput
              name='kind'
              notAsync
              allowCreate={false}
              label={lang.documentExport.forms.export.documentTypes}
              value={kind}
              readOnly={readOnly}
              options={kindOptions}
              containerClass={classes.selectInput}
              customSelectOptions={{
                isClearable: false,
                valueKey: 'id',
              }}
              handleChange={handleKindChange}
            />
            <SelectInput
              name='kindPeriod'
              notAsync
              allowCreate={false}
              label={lang.documentExport.forms.export.periodLabel}
              value={kindPeriod}
              readOnly={readOnly}
              options={periodOptions}
              containerClass={classes.selectInput}
              customSelectOptions={{
                placeholder: lang.documentExport.forms.export.periodPlaceholder,
              }}
              handleChange={handleKindPeriodChange}
            />
            {customKindDateInputs}
          </div>
          {!customKindDateInputs && <div className={classes.action}>
            <ButtonIconSquare
              disabled={submitKindButtonDisabled || readOnly}
              icon={AddIcon}
              label={lang.generics.make}
              onClick={handleKindCreateClick}
            />
          </div>}
        </div>
        {customKindDateInputs && <div className={clsx(classes.inputRow, classes.inputRowEnd)}>
          <div className={classes.action}>
            <ButtonIconSquare
              disabled={submitKindButtonDisabled || readOnly}
              icon={AddIcon}
              label={lang.generics.make}
              onClick={handleKindCreateClick}
            />
          </div>
        </div>}
      </>)
  )

  const controls = client?.isAllowed || !client
    ? (content)
    : (lang.clientDetail.widgets.notAllowed)

  return (controls)
}

DocumentExportForm.financeFormKey = FinanceFormKeys.export

export default DocumentExportForm
